"use client";
import {
  Button,
  Card,
  FileInput,
  FileInputProfile,
  Input,
  ScreenLoader,
  SelectInput,
  SelectSearchInput,
  TextArea,
} from "@/components";
import { useFormStepTwoCompany } from "@/hooks";
import { OptionsCompany } from "@/types";
import { FormProvider } from "react-hook-form";
import { FaGlobe, FaLinkedinIn } from "react-icons/fa6";

type FormStepTwoProp = {
  options: {
    options?: OptionsCompany;
    optionError: boolean;
    optionIsLoading: boolean;
  };
  setStep: (step: number) => void;
};

export default function FormStepTwoCompany({
  options,
  setStep,
}: Readonly<FormStepTwoProp>) {
  const {
    companyInformation,
    methods,
    countryList,
    isLoadingCountry,
    setCountrySearchQuery,
    hasMoreCountries,
    fetchNextCountriesPage,
    emissionCountryList,
    isLoadingEmissionCountry,
    setEmissionCountrySearchQuery,
    hasMoreEmissionCountries,
    fetchNextEmissionCountriesPage,
    statesList,
    isLoadingState,
    onSubmit,
  } = useFormStepTwoCompany(setStep);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(async (data) => await onSubmit(data))}>
        {methods.formState.isSubmitting && <ScreenLoader />}

        <Card>
          <div className="flex flex-col-reverse sm:flex-row">
            {/* Col 1  */}
            <div className="flex-1 flex-col">
              <div className="flex justify-center items-center">
                <FileInputProfile
                  name="picture_profile"
                  fallbackSrc={
                    companyInformation?.picture_profile as string | undefined
                  }
                  required
                />
              </div>
              <TextArea
                name="cover_letter"
                label="Cover Letter"
                rows={10}
                maxLength={1000}
                placeholder="What do you do & why your company is the best place to work.?"
              />
              <div className="mt-4">
                <p className="text-sm text-black font-semibold mb-2">Compliance</p>
                <FileInput
                  name="tax_id_file"
                  label="Upload the Tax ID"
                  existingFileUrl={companyInformation?.kyb?.tax_id_picture_url}
                  comments={companyInformation?.kyb?.comments}
                  acceptedFileTypes={[
                    {
                      type: "document",
                      extensions: [".pdf", ".doc", ".docx", ".txt"],
                      label: "Document files",
                    },
                    {
                      type: "image",
                      extensions: [".png", ".jpg", ".jpeg", ".webp"],
                      label: "Image files",
                    },
                  ]}
                />
              </div>
            </div>
            <div className="flex-2 px-4">
              <h2 className="font-semibold text-2xl mb-4">
                {methods.watch("commercial_name") || "Your Name Company"}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelectInput
                  name="legal_status"
                  options={options.options?.company_legal_status || []}
                  label="Company Legal Status"
                  placeholder="Select the legal status"
                />
                <Input
                  name="legal_name"
                  label="Legal Name"
                  className="w-full"
                  placeholder="Enter your first name"
                />
                <Input
                  name="commercial_name"
                  label="Commercial Name"
                  className="w-full"
                  placeholder="Enter your commercial name"
                  required
                />
                <SelectInput
                  name="size_company"
                  options={options.options?.company_size || []}
                  label="Size of the Company"
                  placeholder="Select the size of the company"
                />
                <SelectSearchInput
                  name="location_country"
                  label="HQ Country"
                  placeholder="Enter the Country of Headquarters"
                  className={`${countryList.length === 0
                    ? "opacity-50 pointer-events-none select-none"
                    : ""
                    }`}
                  required
                  items={countryList}
                  loading={isLoadingCountry}
                  onSearch={setCountrySearchQuery}
                  hasMore={hasMoreCountries}
                  onLoadMore={() => hasMoreCountries && fetchNextCountriesPage()}
                  onResetPage={() => setCountrySearchQuery("")}
                  classNameContainer="w-full z-100"
                />
                <SelectSearchInput
                  name="location_state"
                  label="HQ State"
                  placeholder="Enter the State of HQ"
                  items={statesList}
                  loading={isLoadingState}
                  classNameContainer="w-full z-100"
                />

                <SelectInput
                  name="industry"
                  options={options.options?.company_industry || []}
                  label="Industry"
                  placeholder="Enter the main industry"
                />
                <SelectInput
                  name="company_type"
                  options={options.options?.company_type || []}
                  label="Company type"
                  placeholder="Select the type of company"
                />
              </div>

              <div className="border-t-2 border-secondary-100 my-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="flex justify-center items-center rounded-full bg-blue-500 text-white w-7.5 h-7.5 text-lg mt-4">
                    <FaLinkedinIn />
                  </div>
                  <Input
                    classNameContainer="flex-1"
                    placeholder="Your LinkedIn Link"
                    name="linkedin_url"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="flex justify-center items-center rounded-full bg-gray-500 text-white w-7.5 h-7.5 text-lg mt-4">
                    <FaGlobe />
                  </div>
                  <Input
                    classNameContainer="flex-1"
                    placeholder="Website of the company"
                    name="website_url"
                  />
                </div>
              </div>
              <div className="border-t-2 border-secondary-100 my-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="tax_id"
                  label="Registration Tax ID"
                  className="w-full"
                  placeholder="Enter your Tax ID number"
                />
                <SelectSearchInput
                  name="location_country_emission"
                  label="Country of Emission"
                  placeholder="Enter the Country of Emission"
                  className={`${emissionCountryList.length === 0
                    ? "opacity-50 pointer-events-none select-none"
                    : ""
                    }`}
                  items={emissionCountryList}
                  loading={isLoadingEmissionCountry}
                  onSearch={setEmissionCountrySearchQuery}
                  hasMore={hasMoreEmissionCountries}
                  onLoadMore={() => hasMoreEmissionCountries && fetchNextEmissionCountriesPage()}
                  onResetPage={() => setEmissionCountrySearchQuery("")}
                  classNameContainer="w-full z-100"
                />
              </div>
            </div>
          </div>
        </Card>
        <div className="my-3  flex justify-between md:justify-end gap-4">
          <Button
            type="submit"
            rounded="rounded-xl"
            className="w-52"
            isLoading={methods.formState.isSubmitting}
          >
            Next
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
